import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { AuthTrackingProvider } from "@core/features/tracking";
import useSyncFirebaseToken from "@core/services/api/useSyncFirebaseToken";
import { usePlatformStore } from "./store";
import { PlatformEventListeners } from "./PlatformEventListeners";
import { router } from "./router";
import { useSystemVariable } from "./hooks/useSystemVariable";
import { useIsLoginDisabled } from "./hooks/useIsLoginDisabled";
import type { MarketingSiteMode } from "@core/features/marketing";

export function PlatformApp() {
  const setTokenExpiredStatus = usePlatformStore((s) => s.setTokenExpired);
  const setMarketingSiteMode = usePlatformStore((s) => s.setMarketingSiteMode);

  useSyncFirebaseToken({ setTokenExpiredStatus });

  const setFeatureFlag = usePlatformStore((s) => s.setFeatureFlag);

  // Fetch marketing site mode on app init
  const { data: marketingSiteModeVar } = useSystemVariable(
    "marketing_site_mode"
  );

  // Fetch video disabled flag on app init
  const { data: isVideoDisabledVar } = useSystemVariable("is_video_disabled");

  // Fetch support disabled flag on app init
  const { data: isSupportDisabledVar } = useSystemVariable("is_support_disabled");

  // Update store when system variable is fetched
  useEffect(() => {
    if (marketingSiteModeVar?.value) {
      setMarketingSiteMode(marketingSiteModeVar.value as MarketingSiteMode);
    }
  }, [marketingSiteModeVar, setMarketingSiteMode]);

  // Update video disabled flag when system variable is fetched
  useEffect(() => {
    if (isVideoDisabledVar?.value !== undefined) {
      setFeatureFlag("isVideoDisabled", isVideoDisabledVar.value === "true");
    }
  }, [isVideoDisabledVar, setFeatureFlag]);

  // Update support page disabled flag when system variable is fetched
  useEffect(() => {
    if (isSupportDisabledVar?.value !== undefined) {
      setFeatureFlag("isSupportPageDisabled", isSupportDisabledVar.value === "true");
    }
  }, [isSupportDisabledVar, setFeatureFlag]);

  const user = usePlatformStore((s) => s.user);
  const isTokenExpired = usePlatformStore((s) => s.isTokenExpired);

  // While staff have closed login, anybody still signed in is signed out,
  // wherever they are. Signing out ends on the login page, which then sends
  // them home.
  const { isLoginDisabled } = useIsLoginDisabled();
  useEffect(() => {
    if (isLoginDisabled && user && router.state.location.pathname !== "/logout") {
      router.navigate("/logout", { replace: true });
    }
  }, [isLoginDisabled, user]);

  return (
    <AuthTrackingProvider
      debug={{ enabled: false }}
      user={user || ({} as any)}
      organization={{} as any}
      isTokenExpired={isTokenExpired ?? false}
    >
      <PlatformEventListeners />
      <RouterProvider router={router} />
    </AuthTrackingProvider>
  );
}
